import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Stack, Avatar, ToggleButtonGroup,
  ToggleButton, CircularProgress, IconButton,
} from '@mui/material';
import { Send, SmartToy, Person, Delete } from '@mui/icons-material';
import { aiAPI } from '../../api/client';
import useAuthStore from '../../store/useStore';

function AIChatPage() {
  const { user } = useAuthStore();
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hello! I\'m CampusNest AI Assistant. I can help you with:\n\n- Finding accommodation near your campus\n- Understanding tenancy agreements\n- Payment and deposit questions\n- Roommate matching advice\n- General platform questions\n\nHow can I help you today?' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState('openai');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const history = messages
        .filter(m => m.role !== 'system')
        .map(m => ({ role: m.role, content: m.content }));

      const res = await aiAPI.chat({
        message: userMessage,
        conversation_history: history,
        provider,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I\'m having trouble connecting right now. Please try again later or switch to a different AI provider.',
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: 'Chat cleared. How can I help you?',
    }]);
  };

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>AI Assistant</Typography>
          <Typography variant="body2" color="text.secondary">Get smart recommendations and instant support</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup value={provider} exclusive onChange={(e, v) => v && setProvider(v)} size="small">
            <ToggleButton value="openai" sx={{ textTransform: 'none' }}>ChatGPT</ToggleButton>
            <ToggleButton value="claude" sx={{ textTransform: 'none' }}>Claude</ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={clearChat} size="small" title="Clear chat"><Delete /></IconButton>
        </Stack>
      </Box>

      <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
          <Stack spacing={2}>
            {messages.map((msg, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1.5, flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                <Avatar sx={{
                  bgcolor: msg.role === 'user' ? 'primary.main' : 'secondary.main',
                  width: 36, height: 36,
                }}>
                  {msg.role === 'user' ? <Person /> : <SmartToy />}
                </Avatar>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2, maxWidth: '70%', borderRadius: 2,
                    bgcolor: msg.role === 'user' ? 'primary.50' : 'grey.50',
                    borderColor: msg.role === 'user' ? 'primary.200' : 'grey.200',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                </Paper>
              </Box>
            ))}
            {loading && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}><SmartToy /></Avatar>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <CircularProgress size={20} />
                </Paper>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Stack>
        </Box>

        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth size="small" placeholder="Ask me anything about accommodation..."
              value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
              multiline maxRows={3} disabled={loading}
            />
            <Button variant="contained" onClick={handleSend} disabled={!input.trim() || loading} sx={{ minWidth: 48, px: 1 }}>
              <Send />
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Powered by {provider === 'openai' ? 'ChatGPT' : 'Claude'} &bull; Press Enter to send
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

export default AIChatPage;
